import { cn } from '@/lib/cn';
import { Panel } from './Panel';
import { Skeleton } from './Skeleton';
import { FieldLabel } from './Text';

export interface StatTileProps {
  label: string;
  value: string;
  /** Secondary line, e.g. "+7 vs GW04". Rendered in the accent colour. */
  delta?: string;
  /**
   * Show a placeholder instead of the value.
   *
   * The alternative was an em dash, which is indistinguishable from a real
   * "no data" answer — the reader cannot tell whether to keep waiting.
   */
  isLoading?: boolean;
  /** Lets a caller change how the tile sits in its grid. */
  className?: string;
}

export function StatTile({ label, value, delta, isLoading = false, className }: StatTileProps) {
  return (
    <Panel className={cn('flex min-h-22 flex-col justify-center px-4 py-3.5', className)}>
      <FieldLabel>{label}</FieldLabel>

      {/*
        A countdown like "2d 03h 59m" is several times wider than a score, and
        in a narrow column it used to break across three lines. Dropping a step
        on the smallest screens keeps every tile to one line.

        The placeholder is sized to the line it replaces, so the tile is the
        same height either way.
      */}
      {isLoading ? (
        <Skeleton className="my-1 h-7 w-20 sm:h-8" />
      ) : (
        <div className="my-1 text-xl font-extrabold tracking-[-1px] sm:text-2xl">{value}</div>
      )}

      {delta ? <span className="font-mono text-[11px] text-success">{delta}</span> : null}
    </Panel>
  );
}
